import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useTranslation } from "react-i18next";
import dashboardServices from "../../../services/DashboardServices";
import walletService from "../../../services/WalletService";
import { onPaymentsEvent } from "../../../services/paymentsEvents";
import {
  formatWalletDisplay,
  normalizeBalanceSummaryResponse,
} from "../../../helper/formatAppBalance";
import {
  categoryLabelKey,
  filterLedgerByTab,
} from "../../../helper/walletLedgerUtils";
import { WalletLedgerEntry, WalletLedgerTab } from "../../../types/wallet";
import TopUpWalletModal from "../invoice&payment/TopUpWalletModal";
import { useWalletBalanceVisibility } from "../../../hooks/useWalletBalanceVisibility";
import { NJIM } from "../../../theme/njimTheme";
import PageHeader from "../../../components/ui/PageHeader";
import EmptyState from "../../../components/ui/EmptyState";
import TableSkeleton from "../../../components/ui/TableSkeleton";


const tabKeys: WalletLedgerTab[] = ["all", "moneyIn", "moneyOut"];

const formatLedgerDate = (value: string, locale: string) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const WalletBalance: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.toLowerCase().startsWith("ar");

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<WalletLedgerTab>("all");
  const [entries, setEntries] = useState<WalletLedgerEntry[]>([]);
  const [usesLegacySource, setUsesLegacySource] = useState(false);
  const [ledgerFallbackStatus, setLedgerFallbackStatus] = useState<number | undefined>();
  const [walletBalance, setWalletBalance] = useState(0);
  const [currency, setCurrency] = useState("IQD");
  const [walletMeta, setWalletMeta] = useState({
    customerCode: "",
    title: "",
    customerId: "",
  });
  const [topUpOpen, setTopUpOpen] = useState(false);
  const { balanceVisible, toggleBalanceVisible } = useWalletBalanceVisibility();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [ledgerRes, balanceRes] = await Promise.all([
        walletService.getLedger(),
        dashboardServices.getBalanceSummary().catch(() => null),
      ]);

      setEntries(ledgerRes.items);
      setUsesLegacySource(Boolean(ledgerRes.usedLegacyFallback));
      setLedgerFallbackStatus(ledgerRes.unifiedErrorStatus);

      if (ledgerRes.balance != null) {
        setWalletBalance(ledgerRes.balance);
        if (ledgerRes.currency) setCurrency(ledgerRes.currency);
      } else if (balanceRes?.data) {
        const balances = normalizeBalanceSummaryResponse(balanceRes.data);
        setWalletBalance(balances.appWalletBalance);
        setCurrency(balances.currency);
      } else {
        const walletRes = await dashboardServices.getWalletBalance().catch(() => null);
        const walletData = walletRes?.data;
        if (walletData) {
          setWalletBalance(Number(walletData.balance ?? 0));
          setWalletMeta({
            customerCode: walletData.customerCode ?? "",
            title: walletData.title ?? "",
            customerId: walletData.customerId ?? "",
          });
        }
      }
    } catch (error) {
      console.error("[WalletBalance] load error:", error);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    return onPaymentsEvent(() => {
      loadData();
    });
  }, [loadData]);

  const filteredEntries = useMemo(
    () => filterLedgerByTab(entries, activeTab),
    [entries, activeTab]
  );

  const maskedBalanceDisplay = useMemo(() => {
    const prefix = currency?.trim() ? `${currency.trim()} ` : "IQD ";
    return `${prefix}••••••••`;
  }, [currency]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, direction: isRTL ? "rtl" : "ltr" }}>
      <PageHeader
        title={t("walletPage.title")}
        subtitle={t("walletPage.subtitle")}
        actions={
          <Tooltip title={t("common.refresh", { defaultValue: "Refresh" })}>
            <IconButton
              onClick={loadData}
              disabled={loading}
              aria-label={t("common.refresh")}
              sx={{ color: NJIM.textSecondary, "&:hover": { color: NJIM.ink, bgcolor: NJIM.mintWash } }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />

      <Card
        sx={{
          mt: 3,
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          bgcolor: NJIM.mintWash,
          border: `1px solid rgba(93,183,167,0.28)`,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography sx={{ fontSize: 13, color: NJIM.textSecondary, fontWeight: 500 }}>
              {t("dashboard.walletBalance")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: "2rem", color: NJIM.ink, lineHeight: 1.2 }}>
                {balanceVisible
                  ? formatWalletDisplay(walletBalance, currency)
                  : maskedBalanceDisplay}
              </Typography>
              <Tooltip
                title={
                  balanceVisible
                    ? t("walletPage.hideBalance")
                    : t("walletPage.showBalance")
                }
              >
                <IconButton
                  size="small"
                  onClick={toggleBalanceVisible}
                  aria-label={
                    balanceVisible
                      ? t("walletPage.hideBalance")
                      : t("walletPage.showBalance")
                  }
                  sx={{
                    color: NJIM.textSecondary,
                    "&:hover": { color: NJIM.ink, bgcolor: "rgba(93,183,167,0.12)" },
                  }}
                >
                  {balanceVisible ? (
                    <VisibilityOffIcon sx={{ fontSize: 22 }} />
                  ) : (
                    <VisibilityIcon sx={{ fontSize: 22 }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
            <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, mt: 0.5 }}>
              {t("dashboard.availableForOrders")}
            </Typography>
          </Box>
          <AccountBalanceWalletOutlinedIcon
            sx={{
              fontSize: 36,
              color: NJIM.mint,
              bgcolor: NJIM.mintWash,
              p: 1.25,
              borderRadius: 2,
            }}
          />
        </Box>
        <Button
          variant="contained"
          disableElevation
          disableRipple
          color="inherit"
          className="topup-confirm-btn"
          sx={{
            mt: 2,
            textTransform: "none",
            fontWeight: 600,
          }}
          {...(isRTL ? { endIcon: <AddIcon /> } : { startIcon: <AddIcon /> })}
          onClick={() => setTopUpOpen(true)}
        >
          {t("dashboard.topUp")}
        </Button>
      </Card>

      {usesLegacySource && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.25,
            borderRadius: 2,
            bgcolor: "rgba(245,158,11,0.08)",
            border: "1px solid rgba(245,158,11,0.25)",
          }}
        >
          <Typography sx={{ fontSize: 13, color: NJIM.warning }}>
            {ledgerFallbackStatus
              ? t("walletPage.legacyNoticeError", {
                  status: ledgerFallbackStatus,
                  defaultValue: `Wallet transactions API failed (HTTP ${ledgerFallbackStatus}). Return credits are not shown — only top-ups from payment history. Backend team must fix this endpoint.`,
                })
              : t("walletPage.legacyNotice")}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <Box
          sx={{
            display: "inline-flex",
            p: 0.5,
            borderRadius: 999,
            bgcolor: NJIM.muted,
            border: `1px solid ${NJIM.border}`,
            gap: 0.5,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {tabKeys.map((tab) => {
            const active = activeTab === tab;
            return (
              <Button
                key={tab}
                onClick={() => setActiveTab(tab)}
                sx={{
                  borderRadius: 999,
                  px: 2.5,
                  py: 0.75,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 14,
                  color: active ? NJIM.ink : NJIM.textSecondary,
                  bgcolor: active ? NJIM.mint : "transparent",
                  boxShadow: active ? "0 2px 8px rgba(93,183,167,0.35)" : "none",
                  "&:hover": {
                    bgcolor: active ? NJIM.mintLight : "rgba(255,255,255,0.7)",
                  },
                }}
              >
                {t(`walletPage.tab.${tab}`)}
              </Button>
            );
          })}
        </Box>
      </Box>

      {loading ? (
        <TableSkeleton rows={5} columns={3} />
      ) : filteredEntries.length === 0 ? (
        <EmptyState description={t("walletPage.empty")} />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {filteredEntries.map((entry) => (
            <WalletLedgerRow
              key={entry.id}
              entry={entry}
              locale={i18n.language}
              label={t(categoryLabelKey(entry.category))}
            />
          ))}
        </Box>
      )}

      <TopUpWalletModal
        open={topUpOpen}
        onClose={() => {
          setTopUpOpen(false);
          loadData();
        }}
        WalletData={{
          balance: walletBalance,
          customerCode: walletMeta.customerCode,
          title: walletMeta.title,
          customerId: walletMeta.customerId,
        }}
      />

    </Box>
  );
};

const WalletLedgerRow: React.FC<{
  entry: WalletLedgerEntry;
  locale: string;
  label: string;
}> = ({ entry, locale, label }) => {
  const isIn = entry.direction === "IN";
  const amountColor = isIn ? NJIM.success : NJIM.danger;
  const Icon = isIn ? ArrowDownwardIcon : ArrowUpwardIcon;
  const title =
    entry.category === "RETURN_CREDIT"
      ? label
      : entry.description || label;

  return (
    <Card
      sx={{
        p: 2,
        borderRadius: 2.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        border: `1px solid ${NJIM.border}`,
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 8px 24px rgba(23,34,55,0.06)" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "12px",
            display: "grid",
            placeItems: "center",
            bgcolor: isIn ? "rgba(22,163,74,0.1)" : "rgba(220,38,38,0.08)",
            color: amountColor,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary, fontSize: 15 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, mt: 0.25 }}>
            {formatLedgerDate(entry.date, locale)}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 800, color: amountColor, fontSize: 16 }}>
          {isIn ? "+" : "−"}
          {entry.amount.toLocaleString("en-US", { maximumFractionDigits: 2 })}{" "}
          {entry.currency}
        </Typography>
        <Chip
          size="small"
          label={label}
          sx={{
            mt: 0.5,
            height: 22,
            fontSize: 11,
            fontWeight: 700,
            bgcolor: NJIM.muted,
            color: NJIM.textSecondary,
          }}
        />
      </Box>
    </Card>
  );
};

export default WalletBalance;
